import * as React from 'react';
import { Box, Grid, Stack, ThemeProvider } from '@mui/material';
import { generateTheme } from '../themes';
import { useRef } from 'react';
import { gsap } from "gsap";
import ImageCarousel from './ImageCarousel';
import { RPMButton } from './subComponents/RPMButton';
import { RPMText } from './subComponents/RPMText';
import SchemeBands from './subComponents/SchemeBands';
import YouTubeEmbed from './subComponents/YouTubeEmbed';

// Pull a YouTube video ID out of the common URL formats (watch, youtu.be, embed, shorts, live).
// Returns null for non-YouTube URLs (e.g. self-hosted .mp4), which fall through to the <video> element.
const getYouTubeId = (url) => {
    if (!url || typeof url !== 'string') return null;
    const match = url.match(/(?:youtube(?:-nocookie)?\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([\w-]{11})/);
    return match ? match[1] : null;
};

const TextImageSlice = ({ data }) => {
    const {
        colorTheme = 'white',
        eyebrow = '',
        eyebrowStyle = 'standard',
        pills = [],
        title = '',
        gradientHeadline = false,
        listStyle = 'arrow',
        margins = 'Normal',
        subtitle = '',
        description = '',
        buttonText = '',
        buttonLink = '',
        buttonType = 'filled_primary',
        imageUrl = '',
        videoUrl = '',
        videoFallback = '',
        secondaryImageUrl = '',
        parallax = false,
        sliderImageSize = 'Normal',
        imageSizeMobile = 'Normal',
        textImageAlignment = 'Text left - Image right',
        imageMarginTop = 'None',
        imageMarginBottom = 'None',
        imageMarginTopMobile = 'Normal',
        imageMarginBottomMobile = 'Normal',
        imageAlignment = 'Auto',
        imageMarginLeft = 'None',
        imageMarginRight = 'None',
        imageMarginLeftMobile = 'None',
        imageMarginRightMobile = 'None',
        headlineSize = 'Big',
        sliceMarginTop = 'Auto',
        sliceMarginTopMobile = 'Auto',
        sliceMarginBottom = 'Auto',
        sliceMarginBottomMobile = 'Auto'
    } = data;
    
    // Constants for modes
    const MODE_TEXT = "text";
    const MODE_MEDIA = "media";
    const MODE_TEXT_MEDIA = "text-media";
    const MODE_VIDEO = "video";

    // Use the extracted data directly from props
    const contentData = {
        eyebrow,
        eyebrowStyle,
        pills: Array.isArray(pills) ? pills.filter(Boolean) : [],
        title,
        subtitle,
        description,
        buttonLink: typeof buttonLink === 'string' ? { url: buttonLink } : buttonLink,
        buttonText,
        buttonType,
        images: imageUrl ? [imageUrl] : [],
        video: videoUrl ? {
            url: videoUrl,
            fallbackUrl: videoFallback
        } : null
    };

    // YouTube renders as a contained 16:9 player (no black bars), so it never takes
    // the full-bleed "fill" stretch that self-hosted background videos use.
    const videoIsYouTube = !!getYouTubeId(videoUrl);

    // Layout settings from data properties
    const textMode = textImageAlignment.includes('small') ? 'small' : 'big';
    const imagePosition = textImageAlignment.startsWith('Image') ? 'left' : 'right';
    const imageSize = sliderImageSize === 'Fill entire slice' ? 'fill' :
                     sliderImageSize === 'Large' ? 'large' :
                     sliderImageSize === 'Small' ? 'small' :
                     sliderImageSize === 'Extra Small' ? 'xsmall' : 'normal';
    const imageSizeMob = imageSizeMobile === 'Large' ? 'large' :
                         imageSizeMobile === 'Small' ? 'small' :
                         imageSizeMobile === 'Extra Small' ? 'xsmall' : 'normal';

    const desktopSizes = { xsmall: { h: "30vh", w: "45%" }, small: { h: "45vh", w: "65%" }, normal: { h: "70vh", w: "100%" }, large: { h: "85vh", w: "100%" } };
    const mobileSizes  = { xsmall: { h: "20vh", w: "55%" }, small: { h: "30vh", w: "75%" }, normal: { h: "50vh", w: "100%" }, large: { h: "65vh", w: "100%" } };
    const imgMaxHeight = { xs: mobileSizes[imageSizeMob].h, md: desktopSizes[imageSize]?.h || desktopSizes.normal.h };
    const imgMaxWidth  = { xs: mobileSizes[imageSizeMob].w, md: desktopSizes[imageSize]?.w || desktopSizes.normal.w };

    // Image margin settings
    const marginMap = { 'None': 0, 'Small': '1rem', 'Normal': '2rem', 'Large': '4rem' };
    const imgMarginTop = { xs: marginMap[imageMarginTopMobile] || 0, md: marginMap[imageMarginTop] || 0 };
    const imgMarginBottom = { xs: marginMap[imageMarginBottomMobile] || 0, md: marginMap[imageMarginBottom] || 0 };
    const imgMarginLeft = { xs: marginMap[imageMarginLeftMobile] || 0, md: marginMap[imageMarginLeft] || 0 };
    const imgMarginRight = { xs: marginMap[imageMarginRightMobile] || 0, md: marginMap[imageMarginRight] || 0 };

    // Slice top/bottom margin
    const slicePt = { xs: marginMap[sliceMarginTopMobile] || 0, md: marginMap[sliceMarginTop] || 0 };
    const slicePb = { xs: marginMap[sliceMarginBottomMobile] || 0, md: marginMap[sliceMarginBottom] || 0 };

    // Overall slice spacing — "Small" collapses the 100vh minHeight so slices can stack tightly
    const isCompact = margins === 'Small';
    const textMinHeight = isCompact
        ? { xs: "auto", md: "auto" }
        : (textMode === "big" ? { xs: "50vh", md: "100vh" } : { xs: "50%", md: "100%" });
    const textBoxPadding = isCompact
        ? { xs: "1.5rem", md: "3rem 2rem" }
        : (textMode === "big" ? { xs: "2rem", md: "0" } : { xs: "2rem", md: "6rem 2rem" });
    const textBoxPt = isCompact ? 0 : 3;

    // Image alignment
    const alignMap = { 'Left': 'flex-start', 'Center': 'center', 'Right': 'flex-end' };
    const autoAlign = imagePosition === 'right' ? 'flex-end' : 'flex-start';
    const imgJustify = {
        xs: 'center',
        md: imageAlignment === 'Auto' ? autoAlign : alignMap[imageAlignment] || autoAlign
    };
    // For fill mode, map alignment to object-position-x / background-position-x
    const fillPositionX = imageAlignment === 'Left' ? 'left'
        : imageAlignment === 'Right' ? 'right'
        : 'center';

    // Headline size
    const headlineVariant = headlineSize === 'Small' ? 'h4' :
                            headlineSize === 'Medium' ? 'h3' : 'h2Bold';

    // Text hierarchy styles
    const eyebrowSx = eyebrowStyle === 'preheadline'
        ? {
            display: 'block',
            color: 'text.secondary',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            fontSize: { xs: '1.1rem', md: '1.3rem' },
            fontWeight: 700,
            opacity: 0.85,
            mb: { xs: 2, md: 3 }
        }
        : {
            display: 'block',
            color: 'text.secondary',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            fontSize: '0.8rem',
            fontWeight: 500,
            opacity: 0.7,
            mb: { xs: 1.5, md: 2 }
        };
    const pillsSx = {
        listStyle: 'none',
        display: 'flex',
        flexWrap: 'wrap',
        gap: { xs: 1, md: 1.5 },
        p: 0,
        m: 0,
        mt: { xs: 3, md: 4 }
    };
    const pillSx = {
        px: { xs: 2, md: 2.5 },
        py: { xs: 0.75, md: 1 },
        borderRadius: '999px',
        backgroundColor: '#FFFEFB',
        border: '1px solid rgba(0,0,0,0.08)',
        fontSize: { xs: '0.85rem', md: '0.9rem' },
        fontWeight: 500,
        color: 'text.primary',
        whiteSpace: 'nowrap',
        lineHeight: 1.4
    };
    const titleSx = {
        mb: "1.5rem",
        ...(gradientHeadline ? {
            background: 'linear-gradient(90deg, rgb(6, 194, 220) 0%, rgb(0, 158, 187) 50%, rgb(92, 51, 215) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            color: 'transparent'
        } : {})
    };
    const subtitleSx = {
        mb: "1.5rem",
        fontSize: { xs: "1.3rem", md: "1.5rem" },
        fontWeight: 300,
        lineHeight: 1.2,
        textTransform: "none"
    };
    const ulBulletSx = listStyle === 'checkmark'
        ? {
            content: '"\\2713"',
            position: 'absolute',
            left: '-1.5rem',
            top: '0.05em',
            color: '#0091A5',
            fontWeight: 700,
            fontSize: '1.1em',
            lineHeight: 1.2,
        }
        : {
            content: '""',
            position: 'absolute',
            left: '-1.25rem',
            top: '0.4em',
            width: 0,
            height: 0,
            borderTop: '5px solid transparent',
            borderBottom: '5px solid transparent',
            borderLeft: '7px solid',
            borderLeftColor: 'text.primary',
        };

    const descriptionSx = {
        mb: "1.5rem",
        'ul': {
            pl: listStyle === 'checkmark' ? '2rem' : '2.5rem',
            mb: '0.75rem',
            mt: '0.5rem',
            listStyle: 'none',
        },
        'ol': {
            pl: '1.5rem',
            mb: '0.75rem',
            mt: '0.5rem',
            listStyle: 'none',
            counterReset: 'list-counter',
        },
        'li': {
            mb: listStyle === 'checkmark' ? '0.75rem' : '0.5rem',
            position: 'relative',
            pl: '0.25rem',
        },
        'ul > li::before': ulBulletSx,
        'ol > li::before': {
            counterIncrement: 'list-counter',
            content: 'counter(list-counter)',
            position: 'absolute',
            left: '-1.5rem',
            color: 'primary.main',
            fontWeight: 700,
        },
    };

    // Button settings
    const buttonVariant = contentData.buttonType?.includes('text') ? 'text' :
                         contentData.buttonType === 'outlined_contrast' ? 'outlined-contrast' :
                         contentData.buttonType === 'filled_contrast' ? 'contained-contrast' :
                         contentData.buttonType?.includes('outlined') ? 'outlined' : 'contained';

    // Refs for animations
    const imageRef = useRef(null);
    const imageSliceRef = useRef(null);

    // GSAP parallax animation — fill mode shifts background, non-fill mode does a subtle rise-in
    React.useLayoutEffect(() => {
        if (!imageRef.current || !imageSliceRef.current) return;
        if (!parallax) return;

        const tween = imageSize === "fill"
            ? gsap.fromTo(
                imageRef.current,
                { backgroundPositionY: 0 },
                {
                    backgroundPositionY: "-500px",
                    ease: "none",
                    scrollTrigger: {
                        id: "sliceimage",
                        start: "top bottom",
                        end: "bottom top",
                        trigger: imageSliceRef.current,
                        scrub: true,
                        markers: false,
                    },
                }
            )
            : gsap.fromTo(
                imageRef.current,
                { y: 800, opacity: 0, scale: 0.88 },
                {
                    y: 0,
                    opacity: 1,
                    scale: 1,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: imageSliceRef.current,
                        start: "top 85%",
                        end: "top 20%",
                        scrub: 0.3,
                    },
                }
            );

        return () => {
            tween.scrollTrigger?.kill();
            tween.kill();
        };
    }, [parallax, imageSize]);

    // Determine mode based on content
    let mode = MODE_TEXT_MEDIA;
    if (contentData.video) {
        mode = MODE_VIDEO;
    } else if (!contentData.title && !contentData.subtitle && !contentData.description) {
        mode = MODE_MEDIA;
    } else if (!contentData.images.length) {
        mode = MODE_TEXT;
    }

    const theme = generateTheme(colorTheme);

    // Media component
    const Media = () => {
        if (!contentData.images.length) return null;

        if (contentData.images.length === 1) {
            if (imageSize === "fill") {
                if (parallax) {
                    return (
                        <Box
                            ref={imageRef}
                            sx={{
                                width: "100%",
                                height: { xs: "50vh", md: "100%" },
                                position: { md: "absolute" },
                                top: { md: 0 },
                                left: { md: 0 },
                                backgroundImage: `url(${contentData.images[0]})`,
                                backgroundSize: "cover",
                                backgroundRepeat: "repeat-y",
                                backgroundPositionX: fillPositionX,
                            }}
                        />
                    );
                }
                return (
                    <Box
                        component="img"
                        ref={imageRef}
                        src={contentData.images[0]}
                        loading="lazy"
                        sx={{
                            width: "100%",
                            height: { xs: "50vh", md: "100%" },
                            position: { md: "absolute" },
                            top: { md: 0 },
                            left: { md: 0 },
                            objectFit: "cover",
                            objectPosition: `${fillPositionX} top`,
                            display: "block",
                        }}
                    />
                );
            } else {
                return (
                    <Box
                        sx={{ display: "flex", justifyContent: imgJustify, width: "100%" }}
                    >
                        <Box
                            component="img"
                            ref={imageRef}
                            src={contentData.images[0]}
                            loading="lazy"
                            sx={{
                                maxHeight: imgMaxHeight,
                                maxWidth: imgMaxWidth,
                                width: "auto",
                            }}
                        />
                    </Box>
                );
            }
        } else {
            return (
                <Box ref={imageRef} sx={{ display: "flex", justifyContent: "center" }}>
                    <ImageCarousel
                        imageArray={contentData.images}
                        theme={theme}
                    />
                </Box>
            );
        }
    };

    // Video component
    const Video = ({ url, fallbackurl }) => {
        if (!url) return null;

        // YouTube URLs render as a click-to-play embed (sound on). Everything else is a
        // self-hosted, muted/autoplay/looping background video.
        const youTubeId = getYouTubeId(url);
        if (youTubeId) {
            const plainTitle = typeof title === 'string'
                ? title.replace(/<[^>]+>/g, '').trim()
                : '';
            return (
                <YouTubeEmbed
                    videoId={youTubeId}
                    title={plainTitle || 'YouTube video'}
                    maxWidth={imgMaxWidth}
                    justify={imgJustify}
                />
            );
        }

        const urlType = "video/" + url.split(".").pop();
        const fallbackurlType = fallbackurl ? "video/" + fallbackurl.split(".").pop() : null;

        if (imageSize === "fill") {
            return (
                <Box
                    component="video"
                    autoPlay muted loop playsInline
                    sx={{
                        width: "100%",
                        height: "100%",
                        maxHeight: "100vh",
                        objectFit: "cover",
                        display: "block",
                    }}
                >
                    <source src={url} type={urlType} />
                    {fallbackurl && <source src={fallbackurl} type={fallbackurlType} />}
                </Box>
            );
        }

        return (
            <Box sx={{ display: "flex", justifyContent: imgJustify, width: "100%" }}>
                <Box
                    component="video"
                    autoPlay muted loop playsInline
                    sx={{
                        maxHeight: imgMaxHeight,
                        maxWidth: imgMaxWidth,
                        width: "auto",
                        mb: "-7px"
                    }}
                >
                    <source src={url} type={urlType} />
                    {fallbackurl && <source src={fallbackurl} type={fallbackurlType} />}
                </Box>
            </Box>
        );
    };

    // Render based on mode
    if (mode === MODE_TEXT_MEDIA) {

        
        return (
            <ThemeProvider theme={theme}>
                <Grid
                    container
                    direction={{
                        xs: "column",
                        md: imagePosition === "right" ? "row" : "row-reverse"
                    }}
                    alignItems={{ xs: "stretch", md: "center" }}
                    ref={imageSliceRef}
                    flexWrap="nowrap"
                    spacing={3}
                    sx={{
                        width: "100%",
                        background: (theme) => theme.palette.background.default,
                        overflow: "hidden",
                        position: "relative",
                        m: "0",
                        pt: slicePt,
                        pb: slicePb,
                    }}
                >
                    {colorTheme?.startsWith('colorful') && <SchemeBands />}
                    <Grid item xs={6} md={6} display="flex" sx={{ justifyContent: "center", alignItems: "center", position: "relative", zIndex: 1 }} style={{ padding: "0px" }}>
                        <Box sx={{
                            p: textBoxPadding,
                            maxWidth: { xs: "100%", md: "min-content" },
                            minWidth: textMode === "big" ? "35vw" : "40vw",
                            minHeight: textMinHeight,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            pt: textBoxPt
                        }}>
                            {contentData.eyebrow && (
                                <RPMText content={contentData.eyebrow} variant="overline" elementRenderType="span" sx={eyebrowSx} />
                            )}
                            <RPMText content={contentData.title} variant={headlineVariant} sx={titleSx} />
                            {contentData.subtitle && (
                                <RPMText content={contentData.subtitle} variant="h4" sx={subtitleSx} />
                            )}
                            {contentData.description && (
                                <RPMText content={contentData.description} variant="p" sx={descriptionSx} />
                            )}
                            {contentData.buttonLink?.url && contentData.buttonText && (
                                <RPMButton
                                    link={contentData.buttonLink}
                                    linktext={contentData.buttonText}
                                    type={buttonVariant}
                                />
                            )}
                            {contentData.pills.length > 0 && (
                                <Box component="ul" sx={pillsSx}>
                                    {contentData.pills.map((pill, idx) => (
                                        <Box component="li" key={idx} sx={pillSx}>{pill}</Box>
                                    ))}
                                </Box>
                            )}
                            {secondaryImageUrl && (
                                <Box
                                    component="img"
                                    src={secondaryImageUrl}
                                    alt="Apple Wallet och Google Wallet"
                                    loading="lazy"
                                    sx={{
                                        mt: 3,
                                        maxWidth: { xs: "80%", md: "400px" },
                                        height: "auto",
                                    }}
                                />
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={6} md={6} style={{ padding: "0px" }} sx={{
                        mt: imgMarginTop,
                        mb: imgMarginBottom,
                        ml: imgMarginLeft,
                        mr: imgMarginRight,
                        position: "relative",
                        zIndex: 1,
                        alignSelf: imageSize === "fill" ? { md: "stretch" } : undefined,
                        minHeight: imageSize === "fill" ? { md: "100vh" } : undefined,
                    }}>
                        <Media />
                    </Grid>
                </Grid>
            </ThemeProvider>
        );
    }

    if (mode === MODE_VIDEO) {
        return (
            <ThemeProvider theme={theme}>
                <Grid
                    container
                    direction={{ xs: "column", md: "row" }}
                    alignItems={{ xs: "stretch", md: "center" }}
                    ref={imageSliceRef}
                    flexWrap="nowrap"
                    spacing={3}
                    sx={{
                        width: "100%",
                        background: (theme) => theme.palette.background.default,
                        overflow: "hidden",
                        m: "0",
                        p: "0",
                        maxHeight: imageSize === "fill" && !videoIsYouTube ? "100vh" : "none",
                        pt: slicePt,
                        pb: slicePb,
                    }}
                >
                    <Grid item xs={6} md={6} display="flex" style={{ padding: 0 }} sx={{ justifyContent: "center", alignItems: "center", mt: { xs: "1.5rem", md: "0px" } }}>
                        <Box sx={{
                            px: { xs: "1.5em", md: "0" },
                            maxWidth: { xs: "100%", md: "min-content" },
                            minWidth: textMode === "big" ? "35vw" : "40vw",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center"
                        }}>
                            {contentData.eyebrow && (
                                <RPMText content={contentData.eyebrow} variant="overline" elementRenderType="span" sx={eyebrowSx} />
                            )}
                            <RPMText content={contentData.title} variant={headlineVariant} sx={titleSx} />
                            {contentData.subtitle && (
                                <RPMText content={contentData.subtitle} variant="h4" sx={subtitleSx} />
                            )}
                            {contentData.description && (
                                <RPMText content={contentData.description} variant="p" sx={descriptionSx} />
                            )}
                            {contentData.buttonLink?.url && contentData.buttonText && (
                                <RPMButton
                                    link={contentData.buttonLink}
                                    linktext={contentData.buttonText}
                                    type={buttonVariant}
                                />
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={6} md={6} style={{ padding: 0 }} sx={{
                        mt: imgMarginTop,
                        mb: imgMarginBottom,
                        ml: imgMarginLeft,
                        mr: imgMarginRight,
                        position: "relative",
                        alignSelf: imageSize === "fill" && !videoIsYouTube ? { md: "stretch" } : undefined,
                        minHeight: imageSize === "fill" && !videoIsYouTube ? { md: "100vh" } : undefined,
                    }}>
                        <Video
                            url={contentData.video?.url}
                            fallbackurl={contentData.video?.fallbackUrl}
                        />
                    </Grid>
                </Grid>
            </ThemeProvider>
        );
    }

    if (mode === MODE_TEXT) {
        return (
            <ThemeProvider theme={theme}>
                <Stack sx={{
                    justifyContent: "center",
                    minHeight: isCompact ? "auto" : "100vh",
                    background: (theme) => theme.palette.background.default,
                    px: isCompact ? { xs: 3, md: 5 } : { xs: 3, md: 8 },
                    pt: { xs: `calc(${isCompact ? '24px' : '24px'} + ${marginMap[sliceMarginTopMobile] || '0px'})`, md: `calc(${isCompact ? '40px' : '64px'} + ${marginMap[sliceMarginTop] || '0px'})` },
                    pb: { xs: `calc(${isCompact ? '24px' : '24px'} + ${marginMap[sliceMarginBottomMobile] || '0px'})`, md: `calc(${isCompact ? '40px' : '64px'} + ${marginMap[sliceMarginBottom] || '0px'})` }
                }}>
                    <Box sx={{ maxWidth: { xs: "100%", md: "400px" } }}>
                        {contentData.eyebrow && (
                            <RPMText content={contentData.eyebrow} variant="overline" elementRenderType="span" sx={eyebrowSx} />
                        )}
                        <RPMText content={contentData.title} variant={headlineVariant} sx={titleSx} />
                        {contentData.subtitle && (
                            <RPMText content={contentData.subtitle} variant="h4" sx={subtitleSx} />
                        )}
                        {contentData.description && (
                            <RPMText content={contentData.description} variant="p" sx={descriptionSx} />
                        )}
                        {contentData.buttonLink?.url && contentData.buttonText && (
                            <RPMButton
                                link={contentData.buttonLink}
                                linktext={contentData.buttonText}
                                type={buttonVariant}
                            />
                        )}
                    </Box>
                </Stack>
            </ThemeProvider>
        );
    }

    return null; // MODE_MEDIA returns null as in original
};

export default TextImageSlice;